/**
 * Central UUID registry for every fixture entity.
 *
 * All ids are UUIDv7-SHAPED literals (time-ordered prefix, version nibble 7,
 * variant nibble 8) per SPEC-CONVENTIONS' ASSUMED UUIDv7 convention. Keeping
 * every id here — and importing ONLY ids across fixture modules — prevents
 * circular imports between feature fixtures (orders ↔ quotes, inventory ↔
 * purchasing, …) while letting cross-references hold.
 *
 * Block map (3rd–6th hex chars of the first segment):
 *   a001 uom        a002 attribute   a003 tax-class      a004 category
 *   a005 uom-conv   a012 component   a010 product        a011 variant
 *   a020 party      a021 pay-terms   a022 cust-group     a023 supp-group
 *   a024 address    a025 contact     a030 warehouse      a031 location/bin
 *   a032 movement   a033 transfer    a040 channel        a041 channel-order
 *   a050 sales-ord  a051 so-line     a060 quote          a061 quote-line
 *   a070 po         a071 po-line     a072 grn            a073 grn-line
 *   a074 landed     a075 supp-item   a076 reorder-sugg   a080 pick-task
 *   a081 pick-line  a082 shipment    a083 package        a084 wave
 *   a085 ship-line  a090 rma         a091 rma-line       a092 reason-code
 *   a0a0 staff user a0f0 external refs (finance docs owned by CLAUDE-A)
 */
const fid = (block: string, n: number): string =>
  `0198${block}-0000-7000-8000-${String(n).padStart(12, "0")}`;

/** Deterministic UUIDv7-shaped ids for stateful mock-created records. */
export const fixtureId = fid;

// ── Catalog ──────────────────────────────────────────────────────────────
export const UOM_EA = fid("a001", 1);
export const UOM_BOX = fid("a001", 2);
export const UOM_KG = fid("a001", 3); // deliberately NO conversion path to EA (uom_unconvertible demo)
export const UOMC_BOX_EA = fid("a005", 1);
export const ATTR_COLOR = fid("a002", 1);
export const ATTR_SIZE = fid("a002", 2);
export const TAX_STD = fid("a003", 1);
export const CAT_APPAREL = fid("a004", 1);
export const CAT_ELECTRONICS = fid("a004", 2);
export const CAT_BIKES = fid("a004", 3);
export const PROD_TSHIRT = fid("a010", 1);
export const PROD_CABLE = fid("a010", 2);
export const PROD_WHEEL_SET = fid("a010", 3);
export const PROD_FRAME_KIT = fid("a010", 4);
export const PROD_NEW = fid("a010", 255); // deterministic id returned by mock POST /products
export const VAR_TSHIRT_RED_L = fid("a011", 1);
export const VAR_TSHIRT_BLU_M = fid("a011", 2);
export const VAR_TSHIRT_BLK_S = fid("a011", 3);
export const VAR_CABLE = fid("a011", 4);
export const VAR_WHEEL_SET = fid("a011", 5);
export const VAR_FRAME_KIT = fid("a011", 6);
export const COMP_FRAME_WHEELS = fid("a012", 1);
export const COMP_FRAME_CABLE = fid("a012", 2);

// ── Partners ─────────────────────────────────────────────────────────────
export const PARTY_ACME = fid("a020", 1); // BOTH customer and supplier
export const PARTY_GULF = fid("a020", 2); // customer on credit_hold
export const PARTY_AISHA = fid("a020", 3); // person, channel-matched customer
export const PARTY_RIYADH_METALS = fid("a020", 4); // supplier only
export const PARTY_NEW = fid("a020", 255); // deterministic id for mock POST /parties
export const TERMS_NET30 = fid("a021", 1);
export const TERMS_NET60 = fid("a021", 2);
export const TERMS_DUE_ON_RECEIPT = fid("a021", 3);
export const CG_RETAIL = fid("a022", 1);
export const CG_WHOLESALE = fid("a022", 2);
export const SG_DOMESTIC = fid("a023", 1);
export const ADDR_ACME_BILLING = fid("a024", 1);
export const ADDR_ACME_SHIPPING = fid("a024", 2);
export const ADDR_AISHA_SHIPPING = fid("a024", 3);
export const CONTACT_ACME_BUYER = fid("a025", 1);

// ── Warehouses ───────────────────────────────────────────────────────────
export const WH_RIY = fid("a030", 1);
export const WH_JED = fid("a030", 2);
export const LOC_RECV_01 = fid("a031", 1);
export const LOC_A_01_01 = fid("a031", 2);
export const LOC_A_01_02 = fid("a031", 3);
export const LOC_BULK_01 = fid("a031", 4);

// ── Inventory ────────────────────────────────────────────────────────────
export const MOVE_GRN_TSHIRT = fid("a032", 1);
export const MOVE_GRN_TSHIRT_BLU = fid("a032", 2);
export const MOVE_SHIP_TSHIRT = fid("a032", 3);
export const MOVE_SHIP_CABLE = fid("a032", 4);
export const MOVE_TRANSFER_OUT = fid("a032", 5);
export const MOVE_TRANSFER_IN = fid("a032", 6);
export const MOVE_ADJUST_WHEEL = fid("a032", 7);
export const MOVE_NEW = fid("a032", 255);
export const TRANSFER_RIY_JED = fid("a033", 1);
export const TRANSFER_NEW = fid("a033", 255);

// ── Connectors ───────────────────────────────────────────────────────────
export const CHANNEL_MAGENTO = fid("a040", 1);
export const CO_IMPORTED = fid("a041", 1);
export const CO_SKU_UNMATCHED = fid("a041", 2);
export const CO_CURRENCY_MISSING = fid("a041", 3);
export const CO_DEAD_LETTER = fid("a041", 4);
export const CO_PENDING = fid("a041", 5);

// ── Sales orders ─────────────────────────────────────────────────────────
export const SO_1001 = fid("a050", 1); // imported (channel)
export const SO_1002 = fid("a050", 2); // draft (manual)
export const SO_1003 = fid("a050", 3); // confirmed (from quote Q-0230)
export const SO_1004 = fid("a050", 4); // allocated
export const SO_1005 = fid("a050", 5); // partially_shipped
export const SO_1006 = fid("a050", 6); // held (credit_hold customer)
export const SO_NEW = fid("a050", 255); // mock POST /sales-orders + quote convert
export const SOL_1001_1 = fid("a051", 11);
export const SOL_1002_1 = fid("a051", 21);
export const SOL_1003_1 = fid("a051", 31);
export const SOL_1004_1 = fid("a051", 41);
export const SOL_1005_1 = fid("a051", 51);
export const SOL_1005_2 = fid("a051", 52);
export const SOL_1006_1 = fid("a051", 61);

// ── Quotes ───────────────────────────────────────────────────────────────
export const QUOTE_0229 = fid("a060", 1); // expired
export const QUOTE_0230 = fid("a060", 2); // consumed → SO_1003
export const QUOTE_0231 = fid("a060", 3); // open
export const QUOTE_NEW = fid("a060", 255);
export const QL_0229_1 = fid("a061", 11);
export const QL_0230_1 = fid("a061", 21);
export const QL_0231_1 = fid("a061", 31);

// ── Purchasing ───────────────────────────────────────────────────────────
export const PO_0001 = fid("a070", 1); // draft
export const PO_0002 = fid("a070", 2); // pending_approval
export const PO_0003 = fid("a070", 3); // approved
export const PO_0004 = fid("a070", 4); // sent
export const PO_0005 = fid("a070", 5); // partially_received (has the GRN)
export const PO_0006 = fid("a070", 6); // received
export const PO_0007 = fid("a070", 7); // closed
export const PO_NEW = fid("a070", 255);
export const POL_0001_1 = fid("a071", 11);
export const POL_0002_1 = fid("a071", 21);
export const POL_0003_1 = fid("a071", 31);
export const POL_0004_1 = fid("a071", 41);
export const POL_0005_1 = fid("a071", 51);
export const POL_0005_2 = fid("a071", 52);
export const POL_0006_1 = fid("a071", 61);
export const POL_0007_1 = fid("a071", 71);
export const GRN_0007 = fid("a072", 1);
export const GRNL_0007_1 = fid("a073", 11);
export const GRNL_0007_2 = fid("a073", 12);
export const LC_0001 = fid("a074", 1);
export const SI_ACME_TSHIRT = fid("a075", 1);
export const SI_ACME_CABLE = fid("a075", 2);
export const SI_RM_WHEEL = fid("a075", 3);
export const RS_CABLE_JED = fid("a076", 1);

// ── Warehouse ops ────────────────────────────────────────────────────────
export const WAVE_12 = fid("a084", 1);
export const PT_0001 = fid("a080", 1); // in_progress, assigned
export const PT_0002 = fid("a080", 2); // open, unassigned
export const PTL_0001_1 = fid("a081", 11);
export const PTL_0002_1 = fid("a081", 21);
export const SHP_0001 = fid("a082", 1); // confirmed (sourced the issue movements)
export const SHP_0002 = fid("a082", 2); // packed
export const SHP_0003 = fid("a082", 3); // open
export const SL_0001_1 = fid("a085", 11);
export const SL_0001_2 = fid("a085", 12);
export const SL_0002_1 = fid("a085", 21);
export const SL_0003_1 = fid("a085", 31);
export const PKG_0001_1 = fid("a083", 11);
export const PKG_0002_1 = fid("a083", 21);

// ── RMA ──────────────────────────────────────────────────────────────────
export const RMA_0001 = fid("a090", 1); // resolved (full lifecycle)
export const RMA_0002 = fid("a090", 2); // authorized
export const RMA_0003 = fid("a090", 3); // requested
export const RMA_NEW = fid("a090", 255);
export const RL_0001_1 = fid("a091", 11);
export const RL_0002_1 = fid("a091", 21);
export const RL_0003_1 = fid("a091", 31);
export const RC_DAMAGED = fid("a092", 1);
export const RC_WRONG_ITEM = fid("a092", 2);
export const RC_CHANGED_MIND = fid("a092", 3); // inactive
export const CREDIT_NOTE_REF = fid("a0f0", 1); // finance doc — CLAUDE-A owned, reference only

// ── Product imports (catalog CSV wizard) ─────────────────────────────────
export const IMPORT_VALIDATED = fid("a100", 1);

// ── Staff users ──────────────────────────────────────────────────────────
export const USR_FATIMAH = fid("a0a0", 1);
export const USR_OMAR = fid("a0a0", 2);
